import * as i0 from '@angular/core';
import { Injectable, Inject, Optional, Pipe } from '@angular/core';
import { of, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as i1 from '@angular/common/http';
import * as i1$1 from '@angular/platform-browser';

var GDPREvents;
(function (GDPREvents) {
    GDPREvents["ACCESS_REQUESTED"] = "ACCESS_REQUESTED";
    GDPREvents["RECTIFICATION_REQUESTED"] = "RECTIFICATION_REQUESTED";
    GDPREvents["OPPOSITION_REQUESTED"] = "OPPOSITION_REQUESTED";
    GDPREvents["CANCELATION_REQUESTED"] = "CANCELATION_REQUESTED";
})(GDPREvents || (GDPREvents = {}));

const LANGUAGE_HEADER = 'Accept-Language';
class CountriesService {
    constructor(http) {
        this.http = http;
    }
    getCountries(url, headers) {
        return this.existCountries(headers && headers[LANGUAGE_HEADER])
            ? of(this.countries)
            : this.http.get(url, { headers }).pipe(tap(data => {
                this.countries = data;
                this.language = headers && headers[LANGUAGE_HEADER]?.toUpperCase();
            }));
    }
    getCountry(url, headers, countryCode) {
        const country = countryCode?.toUpperCase();
        return this.existCountries(headers && headers[LANGUAGE_HEADER])
            ? of(this.getPersistedCountry(country))
            : this.http.get(`${url}/${country}`, { headers });
    }
    resetService() {
        this.countries = undefined;
        this.language = undefined;
    }
    existCountries(language) {
        const newLanguage = language?.toUpperCase();
        return newLanguage === this.language && !!this.countries;
    }
    getPersistedCountry(code) {
        return this.countries?.find((item) => item.code === code);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CountriesService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CountriesService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CountriesService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class DatalakeService {
    constructor(http, url) {
        this.http = http;
        this.url = url;
    }
    sendEvent(event, headers, proxyURL) {
        if (!proxyURL && !this.url) {
            return throwError(() => new Error('You should pass a proxy url or inject an url to the service'));
        }
        return this.http.post(proxyURL ?? this.url + '/api/intake', event, {
            headers
        });
    }
    sendGDPREvent(event, headers, proxyURL) {
        if (!proxyURL && !this.url) {
            return throwError(() => new Error('You should pass a proxy url or inject an url to the service'));
        }
        return this.http.post(proxyURL ?? this.url + '/api/intake-gdpr', event, {
            headers
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DatalakeService, deps: [{ token: i1.HttpClient }, { token: 'INTAKE_EVENTS_URL', optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DatalakeService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: DatalakeService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['INTAKE_EVENTS_URL']
                }, {
                    type: Optional
                }] }]; } });

class CountryPipe {
    transform(value, countries) {
        if (!!value) {
            const country = countries.find((item) => item.code === value.toUpperCase());
            return country && country.name;
        }
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CountryPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: CountryPipe, isStandalone: true, name: "country" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: CountryPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'country',
                    standalone: true
                }]
        }] });

class SanitizePipe {
    constructor(sanitizer) {
        this.sanitizer = sanitizer;
    }
    transform(value, type) {
        switch (type) {
            case 'html':
                return this.sanitizer.bypassSecurityTrustHtml(value);
            case 'style':
                return this.sanitizer.bypassSecurityTrustStyle(value);
            case 'script':
                return this.sanitizer.bypassSecurityTrustScript(value);
            case 'url':
                return this.sanitizer.bypassSecurityTrustUrl(value);
            case 'resourceUrl':
                return this.sanitizer.bypassSecurityTrustResourceUrl(value);
            default:
                throw new Error(`Invalid safe type specified: ${type}`);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SanitizePipe, deps: [{ token: i1$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SanitizePipe, isStandalone: true, name: "sanitize" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SanitizePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'sanitize',
                    standalone: true
                }]
        }], ctorParameters: function () { return [{ type: i1$1.DomSanitizer }]; } });

/*
 * Public API Surface of common
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CountriesService, CountryPipe, DatalakeService, GDPREvents, SanitizePipe };
